import React, { useEffect, useState } from 'react'
import {
    Box,
    Text,
    Drawer,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    DrawerOverlay,
    DrawerContent,
    IconButton,
    Button,
    Link,

    /* Hooks */
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { FormattedMessage } from 'react-intl'

/* Styles */
import { useStoreLocatorStyles } from '../styles/storeLocatorStyles'

/* Hooks */
import { useStoreLocator } from '../../../contexts/store-locator-context';

/* Icons */
import { CustomClose } from '../../../components/icons'

/* Configs */
import { STORE_LOCATOR_CONFIGS } from '../configs'

/* Helpers */
import { formatGroupedHours } from '../../../utils/storelocator-utils'

const StoreLocatorDetails = () => {
    /* Styling */
    const styles = useStoreLocatorStyles()
    const storeDetailsStyling = styles?.components?.storeDetails
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const drawerPlacement = useBreakpointValue({ base: 'bottom', lg: 'right' })
    const [formattedHours, setFormattedHours] = useState(null)
    const [hoursStatus, setHoursStatus] = useState(null)
    const {
        selectedStore,
        isOpenStoreDetails,
        onCloseStoreDetails,
        onOpenStoreDetails,
        getLocalizedHoursStatus
    } = useStoreLocator()

    useEffect(() => {
        if (selectedStore) {
            setFormattedHours(formatGroupedHours(selectedStore))
            setHoursStatus(getLocalizedHoursStatus(selectedStore))
        }
    }, [selectedStore])

    return (
        <Drawer
            placement={drawerPlacement}
            isOpen={isOpenStoreDetails}
            onClose={onCloseStoreDetails}
            onOpen={onOpenStoreDetails}
            size={isMobile ? 'auto' : 'md'}
            scrollBehavior="inside"
            isFullHeight={true}
        >
            <DrawerOverlay />
            <DrawerContent {...storeDetailsStyling.drawerContent} height={isMobile ? '85dvh' : 'auto'} maxHeight={isMobile ? '85dvh' : 'auto'} >
                <DrawerHeader {...storeDetailsStyling.drawerHeader}>
                    <IconButton
                        aria-label="Close"
                        icon={<CustomClose />}
                        {...storeDetailsStyling.drawerCloseIcon}
                        onClick={onCloseStoreDetails}
                    />
                </DrawerHeader>
                <DrawerBody {...storeDetailsStyling.drawerBody}>
                    <Box {...storeDetailsStyling.blockSpacing}>
                        <Text {...storeDetailsStyling.name}>
                            {selectedStore?.name}
                        </Text>
                        <Text {...storeDetailsStyling.address}>
                            {selectedStore?.address1}
                        </Text>
                        <Text {...storeDetailsStyling.address}>
                            {selectedStore?.city}
                            {(selectedStore?.stateCode || selectedStore?.state) ? `, ${selectedStore?.stateCode || selectedStore?.state}` : ''}
                            {selectedStore?.postalCode ? `, ${selectedStore?.postalCode}` : ''}
                        </Text>
                        {!isMobile &&
                            <Box {...storeDetailsStyling.statusWrapper}>
                                <Text {...styles.hours} {...storeDetailsStyling.hourStatus}>
                                    {hoursStatus?.status === 'OPEN_UNTIL' ? (
                                        <Box as='span' {...styles.greenDot} {...styles.dot}></Box>
                                    ) : (
                                        <Box as='span' {...styles.redDot} {...styles.dot}></Box>
                                    )}
                                    {hoursStatus?.message}
                                </Text>
                                <Button as={Link} variant="webTertiary" {...storeDetailsStyling.getDirections}
                                    href={STORE_LOCATOR_CONFIGS.getDirectionURL + selectedStore?.latitude + ',' + selectedStore?.longitude}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    <FormattedMessage
                                        defaultMessage="Get Directions"
                                        id="store_locator.get-directions"
                                    />
                                </Button>
                            </Box>
                        }
                    </Box>

                    <Box {...storeDetailsStyling.blockSpacing}>
                        {formattedHours?.length > 0 &&
                            <>
                                <Text {...storeDetailsStyling.title}>
                                    <FormattedMessage
                                        id='store_locator.details.store_hours'
                                        defaultMessage="Opening Hours"
                                    />
                                </Text>
                                <Box>
                                    {formattedHours?.map((hourLine, index) => (
                                        <Box key={`hour-${index}`} {...storeDetailsStyling.content}
                                            dangerouslySetInnerHTML={{ __html: hourLine }} />
                                    ))}
                                </Box>
                            </>
                        }
                    </Box>
                    {selectedStore?.c_storeHolidays &&
                        <Box {...storeDetailsStyling.blockSpacing}>
                            <Text {...storeDetailsStyling.title}>
                                <FormattedMessage
                                    id='store_locator.details.store_holidays'
                                    defaultMessage="Store holidays"
                                />
                            </Text>
                            <Box>
                                {selectedStore?.c_storeHolidays?.map((holyday, index) => {
                                    return <Text key={`holiday-${index}`} {...storeDetailsStyling.content}>
                                        {holyday}
                                    </Text>
                                })}
                            </Box>
                        </Box>
                    }
                    {selectedStore?.c_parkingLotAvailable &&
                        <Box {...storeDetailsStyling.blockSpacing}>
                            <Text {...storeDetailsStyling.title}>
                                <FormattedMessage
                                    id='store_locator.details.parking_lot'
                                    defaultMessage="Parking lot"
                                />
                            </Text>
                            <Box>
                                <Text>
                                    <FormattedMessage
                                        id='store_locator.details.parking_lot_status'
                                        defaultMessage="Parking lot available"
                                    />
                                </Text>
                            </Box>
                        </Box>
                    }
                </DrawerBody>

                {isMobile &&
                    <DrawerFooter {...storeDetailsStyling.drawerFooter}>
                        {selectedStore?.phone &&
                            <Button as={Link} variant="webPrimary" {...storeDetailsStyling.cta}
                                href={`tel:${selectedStore?.phone}`}>
                                <FormattedMessage
                                    defaultMessage="Call Store"
                                    id="store_locator.call_store"
                                />
                            </Button>
                        }
                        <Button as={Link} variant="webSecondaryOutline" {...storeDetailsStyling.cta}
                            href={STORE_LOCATOR_CONFIGS.getDirectionURL + selectedStore?.latitude + ',' + selectedStore?.longitude}
                            target="_blank"
                            rel="noopener noreferrer">
                            <FormattedMessage
                                defaultMessage="Get Directions"
                                id="store_locator.get-directions"
                            />
                        </Button>
                    </DrawerFooter>
                }
            </DrawerContent>
        </Drawer>
    )
}

export default StoreLocatorDetails
